<template>
  <el-dialog
    v-dialog-drag-resize
    :visible.sync="localVisible"
    top="5vh"
    custom-class="gen-dialog"
    :close-on-click-modal="false"
    :show-close="false"
    append-to-body
  >
    <template #title>
      <div class="gen-dialog-header">
        <span class="gen-dialog-title">报告生成</span>
        <i class="dialog-close-btn" @click="localVisible = false" />
      </div>
    </template>

    <!-- 分段式 Tab 栏 -->
    <div class="gen-tab-bar">
      <div class="gen-tab-track">
        <div
          v-for="tab in tabs"
          :key="tab.key"
          class="gen-tab-item"
          :class="{ 'is-active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </div>
      </div>
    </div>

    <!-- Tab 内容区 -->
    <div class="gen-tab-content">
      <report-gen-sign-in v-if="activeTab === 'signIn'" :rrma-id="rrmaId" />
      <report-gen-attendance v-else-if="activeTab === 'attendance'" :rrma-id="rrmaId" />
      <report-gen-evaluation v-else-if="activeTab === 'evaluation'" :rrma-id="rrmaId" :rating-up-type="ratingUpType" :detail-data="detailData" />
      <report-gen-summary v-else-if="activeTab === 'summary'" :rrma-id="rrmaId" />
    </div>

    <template #footer>
      <el-button size="small" @click="localVisible = false">关 闭</el-button>
    </template>
  </el-dialog>
</template>

<script>
import ReportGenSignIn from './reportGenSignIn.vue'
import ReportGenAttendance from './reportGenAttendance.vue'
import ReportGenEvaluation from './reportGenEvaluation.vue'
import ReportGenSummary from './reportGenSummary.vue'
import { getRtReportModelApplyDetailsUser } from '@/api/api'

export default {
  name: 'ReportGenerate',
  components: { ReportGenSignIn, ReportGenAttendance, ReportGenEvaluation, ReportGenSummary },
  props: {
    visible: { type: Boolean, default: false },
    rrmaId: { type: [Number, String], default: null }
  },
  data () {
    return {
      localVisible: this.visible,
      activeTab: 'signIn',
      // 详情数据（弹框打开时调用，传给子组件判断评分类型）
      detailData: null,
      ratingUpType: null,
      tabs: [
        { key: 'signIn', label: '签到表', icon: 'el-icon-document-checked' },
        { key: 'attendance', label: '考勤表', icon: 'el-icon-date' },
        { key: 'evaluation', label: '评估表', icon: 'el-icon-data-analysis' },
        { key: 'summary', label: '总结报告', icon: 'el-icon-document' }
      ]
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val
      // 弹框打开时调用详情接口获取评分类型
      if (val && this.rrmaId) this.fetchDetail()
    },
    localVisible (val) {
      this.$emit('update:visible', val)
      // 弹框关闭时重置 tab 和详情数据
      if (!val) {
        this.activeTab = 'signIn'
        this.detailData = null
        this.ratingUpType = null
      }
    },
    // tab 切换时保护拖拽/拉伸后的弹框位置，防止 Vue :style 重渲染覆盖
    activeTab () {
      const dialog = this.$el?.querySelector('.el-dialog')
      if (!dialog || dialog.style.position !== 'fixed') return
      const { left, top, width, height } = dialog.style
      this.$nextTick(() => {
        dialog.style.left = left
        dialog.style.top = top
        if (width) dialog.style.width = width
        if (height) dialog.style.height = height
        dialog.style.margin = '0'
      })
    }
  },
  methods: {
    // 弹框打开时调用详情接口，获取评分类型传递给评估表子组件
    async fetchDetail () {
      try {
        const res = await getRtReportModelApplyDetailsUser({ rrmaId: this.rrmaId })
        this.detailData = res.data || null
        this.ratingUpType = (res.data && res.data.ratingUpType) || null
      } catch {
        this.detailData = null
        this.ratingUpType = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// 弹框头部
.gen-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #eef1f6;
}

.gen-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}


// Tab 栏 — 分段式控件
.gen-tab-bar {
  margin-bottom: 20px;
}

.gen-tab-track {
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
}

.gen-tab-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;

  i { font-size: 15px; }

  &:hover:not(.is-active) {
    color: #475569;
    background: rgba(255, 255, 255, 0.6);
  }

  &.is-active {
    color: #fff;
    font-weight: 600;
    background: #4f7cff;
    box-shadow: 0 2px 8px rgba(79, 124, 255, 0.3);
  }
}

// 内容区
.gen-tab-content {
  min-height: 260px;
  overflow-x: hidden;
}
</style>

<style lang="scss">
// 弹框全局样式（不可 scoped）
.gen-dialog {
  width: 90vw;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.12);
  max-width: calc(100vw - 32px);

  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }

  .el-dialog__body {
    padding: 20px 24px 8px;
  }

  .el-dialog__footer {
    padding: 14px 24px;
    border-top: 1px solid #eef1f6;
  }
}
</style>
